import React, { useState } from 'react'
import ScannerInput from './ScannerInput.jsx'
import { ApiError } from '../../utils/api'
import {
  commitMove as commitMoveRequest,
  commitPutaway as commitPutawayRequest,
  defaultPositionFor,
  lookupShelf
} from '../../utils/warehouseApi'

// Warehouse team asked to drop the move-reason picker for now -- it
// slowed every move down and isn't useful audit context yet. We send a
// fixed reason on every move; MoveReasonPicker.jsx is left in the repo
// (with its tests) so re-enabling is a one-line change if the team
// changes its mind.
const DEFAULT_MOVE_REASON = 'reorganisation'

// Step state machine: 'scan' -> 'committing' -> done
function PutawayScannerModal({ item, onSuccess, onCancel }) {
  const isMove = !!item.current_shelf_id
  const [step, setStep] = useState('scan')
  const [lookupError, setLookupError] = useState(null)
  const [commitError, setCommitError] = useState(null)

  async function onShelfScanned(shelfId) {
    setLookupError(null)
    try {
      const meta = await lookupShelf(shelfId)
      if (meta.status === 'full') {
        setLookupError('Shelf is full. Scan another.')
        return
      }
      if (meta.status === 'deleted') {
        setLookupError('Shelf no longer exists. Scan another.')
        return
      }
      if ((meta.shelf_type ?? meta.type) === 'tote_rack') {
        setLookupError('Tote rack putaway is not supported in Phase 0b. Scan another.')
        return
      }

      // Backend returns shelf_type (snake_case Pydantic field). We accept
      // either spelling defensively so a future contract tweak doesn't
      // re-trigger this bug.
      const position = defaultPositionFor(meta.shelf_type ?? meta.type)

      if (isMove) {
        commitMove(meta.shelf_id, position)
      } else {
        commitPutaway(meta.shelf_id, position)
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        // Backend auto-creates unknown shelves as large_bay on commit.
        // No metadata to check — proceed with the operator's input and
        // let the commit handler create it. Position omitted so backend
        // autoincrements.
        const position = defaultPositionFor('large_bay')
        if (isMove) {
          commitMove(shelfId, position)
        } else {
          commitPutaway(shelfId, position)
        }
        return
      }
      setLookupError(e.message || 'Could not look up shelf.')
    }
  }

  async function commitPutaway(shelfId, position) {
    setCommitError(null)
    setStep('committing')
    try {
      // For large_bay we omit position; backend assigns and returns it.
      // For standard_bay backend echoes our "L". commitPutaway resolves both.
      onSuccess(await commitPutawayRequest({ itemId: item.item_id, shelfId, position }))
    } catch (e) {
      setCommitError(e.message || 'Commit failed.')
      setStep('scan')
    }
  }

  async function commitMove(shelfId, position) {
    setCommitError(null)
    setStep('committing')
    try {
      // This flow never asks the operator to confirm where the item started,
      // so it sends no opinion on that — see commitMove.
      onSuccess(await commitMoveRequest({
        itemId: item.item_id,
        fromShelf: item.current_shelf_id,
        fromPosition: item.current_position,
        toShelf: shelfId,
        toPosition: position,
        reason: DEFAULT_MOVE_REASON
      }))
    } catch (e) {
      setCommitError(e.message || 'Commit failed.')
      setStep('scan')
    }
  }

  // Once a commit is in flight we cannot abort it (the api util has no
  // signal). Block all cancel paths so the user cannot walk away from a
  // successful POST and miss the toast / double-tap into a 409.
  const dismissible = step !== 'committing'

  return (
    <div className="modal-overlay" onClick={dismissible ? onCancel : undefined}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 480, width: '100%' }}
      >
        <div className="modal-header">
          <h3 style={{ margin: 0, fontSize: 18 }}>
            {isMove ? 'Move' : 'Put away'}: {item.title}
          </h3>
          <button
            className="modal-close"
            onClick={onCancel}
            disabled={!dismissible}
            aria-label="Close"
          >×</button>
        </div>

        <div className="modal-body-scroll">
          {step === 'scan' && (
            <>
              <ScannerInput
                onResolve={onShelfScanned}
                onCancel={onCancel}
              />
              {lookupError && (
                <div style={{ color: '#dc2626', fontSize: 14, marginTop: 12, textAlign: 'center' }}>
                  {lookupError}
                </div>
              )}
              {commitError && (
                <div style={{ color: '#dc2626', fontSize: 14, marginTop: 12, textAlign: 'center' }}>
                  {commitError}
                </div>
              )}
            </>
          )}

          {step === 'committing' && (
            <div style={{ textAlign: 'center', padding: 24 }}>
              <div className="loading-spinner" />
              <div style={{ marginTop: 12 }}>Saving…</div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PutawayScannerModal
